import React, { useState, useEffect } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import DataNotFound from "components/data-not-found";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { MoreSvg } from "assets/images/svgs";
import TicketPopup from "components/popups/pass-popup";
import useAttendee from "containers/attendee/useHooks";
import CancellationConfirmationPopup from "./CancellationConfirmationPopup";
import { confirmationPopup } from "utils/helpers/common/alert-service";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  subscriptions: MinorDTO[];
  isLoading?: boolean;
  eventID?: number;
  refreshEvent?: () => void;
  eventData?: any;
};

const JoiningMembersPopup: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  subscriptions,
  isLoading = false,
  eventID,
  refreshEvent,
  eventData,
}) => {
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [isTicketPopupOpen, setIsTicketPopupOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState<MinorDTO | null>(
    null
  );
  const [isCancellationPopupOpen, setIsCancellationPopupOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<number | null>(null);

  const { removeJoiningMember } = useAttendee();

  const toggleDropdown = (id: number) => {
    setOpenDropdownId((prev) => (prev === id ? null : id));
  };
  // Removed useOutsideClick to prevent interference with child modals

  // Close popup if no subscriptions remain after removal
  useEffect(() => {
    if (isOpen && subscriptions?.length === 0 && !isLoading) {
      onClose();
    }
  }, [subscriptions, isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  // Check if event time has passed
  const isEventTimePassed = () => {
    if (
      !eventData?.workshopSessions ||
      eventData.workshopSessions.length === 0
    ) {
      return false;
    }

    const now = new Date();
    const latestSession = eventData.workshopSessions.reduce(
      (latest: any, current: any) => {
        const latestEndTime = new Date(latest.endDateTime);
        const currentEndTime = new Date(current.endDateTime);
        return currentEndTime > latestEndTime ? current : latest;
      }
    );

    const eventEndTime = new Date(latestSession.endDateTime);
    return now > eventEndTime;
  };

  // const handleRemoveMember = (minorId: number) => {
  //   removeJoiningMember(eventID, minorId);
  //   refreshEvent();

  //   setOpenDropdownId(null);
  // };

  const handleRemoveMember = async (minorId: number) => {
    setOpenDropdownId(null);

    // Check if event is a session or workshop
    const isSession = eventData?.isSession === true;

    if (isSession) {
      // For sessions: show simple confirmation popup
      const result = await confirmationPopup(
        "Are you sure you want to cancel this event?",
        "Yes",
        "No"
      );

      if (result.isConfirmed) {
        await removeJoiningMember(eventID, minorId);
        if (refreshEvent) {
          await refreshEvent();
        }
      }
    } else {
      // For workshops: show cancellation confirmation modal
      setMemberToRemove(minorId);
      setIsCancellationPopupOpen(true);
    }
  };

  const handleConfirmCancellation = async () => {
    if (!memberToRemove) return;

    await removeJoiningMember(eventID, memberToRemove);
    if (refreshEvent) {
      await refreshEvent();
    }

    setMemberToRemove(null);
  };

  return (
    <>
      <div
        className={`fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[380px] h-auto transform transition-transform duration-300 ${
            isOpen ? "scale-100" : "scale-95"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}

          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-all duration-200"
          >
            <AiOutlineClose size={18} />
          </button>

          <h2 className="text-base text-center text-primaryBlueText font-bold mb-4">
            Members Joining in this Experience
          </h2>

          <div className="flex flex-col ">
            {subscriptions?.map((user, index) => (
              <div
                className="flex justify-between border-b border-gray-100 items-center"
                key={user.id}
              >
                <div
                  key={`${user.id}-${index}`}
                  className="flex items-center gap-4  py-4"
                >
                  <div>
                    <img
                      className="w-[45px] h-[45px] rounded-full object-cover"
                      src={concatPathWithBackendUrl(
                        user?.profilePicUrl?.fileDownloadUri
                      )}
                      alt={`${user.firstName} ${user.lastName}`}
                    />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-primaryBlueText font-semibold text-sm">
                      {`${user.firstName} ${user.lastName}`}
                    </h2>
                    <p className="text-labelTextColor text-xs">{user.username}</p>
                  </div>
                </div>
                <div className="relative pt-4 ">
                  <div
                    onClick={() => toggleDropdown(user?.id)}
                    className="cursor-pointer"
                  >
                    <MoreSvg />
                  </div>

                  {openDropdownId === user.id && (
                    <div className="absolute right-0 top-10 bg-white border rounded shadow-md text-xs w-32 z-50">
                      <button
                        onClick={() => {
                          setSelectedSubscriber(user);
                          setIsTicketPopupOpen(true);
                          setOpenDropdownId(null);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                          !isEventTimePassed() ? "border-b" : ""
                        }`}
                      >
                        View Pass
                      </button>
                      {!isEventTimePassed() && (
                        <button
                          onClick={() => handleRemoveMember(user.id)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                        >
                          Cancel Event
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="my-5">
            <DataNotFound show={!isLoading && !subscriptions.length} />
          </div>
        </div>
      </div>
      {isTicketPopupOpen && (
        <TicketPopup
          isOpen={isTicketPopupOpen}
          onClose={() => {
            setIsTicketPopupOpen(false);
            setSelectedSubscriber(null);
          }}
          eventData={eventData}
          selectedSubscriber={selectedSubscriber}
        />
      )}
      {isCancellationPopupOpen && (
        <CancellationConfirmationPopup
          isOpen={isCancellationPopupOpen}
          onClose={() => {
            setIsCancellationPopupOpen(false);
            setMemberToRemove(null);
          }}
          onConfirm={handleConfirmCancellation}
          eventData={eventData}
        />
      )}
    </>
  );
};

export default JoiningMembersPopup;
